import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { P, PD, PL, A } from './BlogShared.jsx';

/**
 * Floating glass capsule section tracker.
 * Frosted-glass pill with icons, gradient glow on active,
 * tooltip labels on hover, progress fill as you scroll.
 * Desktop only.
 */
const sections = [
  { id: 'hero', label: 'The Edge', icon: '⚡' },
  { id: 'access-tiers', label: 'Your Team', icon: '🛡️' },
  { id: 'inventory', label: 'Inventory', icon: '📦' },
  { id: 'waste', label: 'Zero Waste', icon: '♻️' },
  { id: 'all-in-one', label: 'Platform', icon: '🧩' },
  { id: 'all-devices', label: 'Any Device', icon: '📱' },
  { id: 'comparison', label: 'Why Us', icon: '⭐' },
  { id: 'closing', label: 'Get Started', icon: '🚀' },
];

export const SectionTracker = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [hoveredIdx, setHoveredIdx] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + window.innerHeight * 0.35;
      let current = 0;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i].id);
        if (el && el.offsetTop <= scrollY) {
          current = i;
          break;
        }
      }
      setActiveIdx(current);

      // Overall page scroll progress (0–1)
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? Math.min(window.scrollY / docHeight, 1) : 0);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const ITEM_H = 44; // height per icon slot
  const capsuleH = sections.length * ITEM_H + 20; // +20 for top/bottom padding

  return (
    <Box
      sx={{
        position: 'fixed',
        left: { md: 20, lg: 32 },
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 90,
        display: { xs: 'none', md: 'block' },
      }}
    >
      {/* Glass capsule container */}
      <Box
        sx={{
          position: 'relative',
          width: 46,
          height: capsuleH,
          borderRadius: '24px',
          background: 'rgba(255,255,255,0.65)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.5)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.6)',
          overflow: 'hidden',
        }}
      >
        {/* Scroll progress fill — rises from bottom */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%',
            height: `${scrollProgress * 100}%`,
            background: `linear-gradient(to top, ${P}10, ${A}08, transparent)`,
            transition: 'height 0.15s linear',
            borderRadius: '24px',
            pointerEvents: 'none',
          }}
        />

        {/* Active glow backdrop behind active icon */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: 10 + activeIdx * ITEM_H + ITEM_H / 2,
            transform: 'translate(-50%, -50%)',
            width: 36,
            height: 36,
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${P}25, ${A}20)`,
            boxShadow: `0 0 16px ${P}20, 0 0 4px ${A}15`,
            transition: 'top 0.45s cubic-bezier(.16,1,.3,1)',
            pointerEvents: 'none',
          }}
        />

        {/* Section icons */}
        <Box sx={{ position: 'relative', pt: '10px', pb: '10px' }}>
          {sections.map((s, i) => {
            const isActive = i === activeIdx;
            const isHovered = i === hoveredIdx;
            return (
              <Box
                key={s.id}
                onClick={() => scrollTo(s.id)}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                sx={{
                  height: ITEM_H,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.25s ease',
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    fontSize: isActive ? '1.1rem' : '0.85rem',
                    transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
                    transform: isActive ? 'scale(1.15)' : isHovered ? 'scale(1.1)' : 'scale(1)',
                    filter: isActive
                      ? 'drop-shadow(0 0 6px rgba(69,39,160,0.4))'
                      : 'grayscale(0.6) opacity(0.5)',
                    lineHeight: 1,
                  }}
                >
                  {s.icon}
                </Box>

                {/* Tooltip label — slides out on hover */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: '100%',
                    ml: 1.2,
                    px: 1.5,
                    py: 0.7,
                    borderRadius: '10px',
                    background: isActive
                      ? `linear-gradient(135deg, ${PD}, ${P})`
                      : 'rgba(30,20,50,0.85)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    opacity: isHovered ? 1 : 0,
                    transform: isHovered ? 'translateX(0)' : 'translateX(-8px)',
                    transition: 'all 0.25s cubic-bezier(.16,1,.3,1)',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    // Arrow
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: -4,
                      top: '50%',
                      transform: 'translateY(-50%) rotate(45deg)',
                      width: 8,
                      height: 8,
                      background: isActive ? PD : 'rgba(30,20,50,0.85)',
                      borderRadius: '2px',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#fff',
                      letterSpacing: '0.02em',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {s.label}
                  </Typography>
                </Box>

                {/* Active dot indicator at bottom */}
                {isActive && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 2,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${P}, ${A})`,
                      boxShadow: `0 0 6px ${A}60`,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Scroll percentage — below capsule */}
      <Typography
        sx={{
          textAlign: 'center',
          mt: 1.5,
          fontSize: '0.6rem',
          fontWeight: 700,
          color: `${P}80`,
          letterSpacing: '0.05em',
          fontFamily: 'monospace',
          transition: 'color 0.3s',
        }}
      >
        {Math.round(scrollProgress * 100)}%
      </Typography>
    </Box>
  );
};
